import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { usePresence } from '../shell/presence.js'

/**
 * PREMIER DIALOGUE du design system — il devient LA référence (spéc portée
 * dans docs/design/live-control-design-system-v1.md, §Dialogues).
 *
 * Règles fixées ici :
 * · overlay navy translucide + léger flou — le fond reste identifiable
 * · carte 440 px max, --radius-card, arête lumineuse teintée par l'enjeu
 * · titre = la QUESTION, texte = la CONSÉQUENCE, actions à droite,
 *   l'action dangereuse en dernier et jamais pré-focalisée
 * · Échap ferme, le focus est piégé, retour au déclencheur à la fermeture
 */
export function ConfirmDialog(props: {
  open: boolean
  title: string
  danger?: boolean
  confirmLabel: string
  /** « Annuler » ne dit pas toujours ce qu'on récupère : « Continuer l'édition » si. */
  cancelLabel?: string
  onConfirm: () => void
  onClose: () => void
  children: ReactNode
}) {
  const cancelRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  // Sortie animée (LOT 5.5.2) — la surface reste montée le temps du fondu.
  const presence = usePresence(props.open, 200)

  useEffect(() => {
    // `presence.mounted` DOIT figurer dans les dépendances : au premier rendu
    // où `open` passe à true, la présence n'a pas encore monté le panneau, le
    // composant renvoie null et `cancelRef` est vide — le focus n'allait donc
    // nulle part, et restait sur <body>. Sur une modale destructive, c'est
    // grave : au clavier, plus rien n'indique où l'on est.
    if (!props.open || !presence.mounted) return
    const previous = document.activeElement as HTMLElement | null
    cancelRef.current?.focus() // le focus initial va sur l'action SÛRE
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose()
      if (e.key === 'Tab') {
        // Piège à focus minimal : le dialogue n'a que des boutons.
        const focusables = dialogRef.current?.querySelectorAll<HTMLElement>('button')
        if (!focusables?.length) return
        const first = focusables[0]!
        const last = focusables[focusables.length - 1]!
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      previous?.focus()
    }
  }, [props.open, presence.mounted]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!presence.mounted) return null

  // PORTAL vers <body> (LOT 5.5.2) : un parent avec transform (sidebar en
  // expansion) créerait un containing block et coincerait l'overlay fixed.
  return createPortal(
    <div className="lc-dialog-overlay" data-state={presence.state} onClick={props.onClose} role="presentation">
      <div
        ref={dialogRef}
        data-state={presence.state}
        className={`lc-dialog${props.danger ? ' lc-dialog--danger' : ''}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="lc-dialog-title"
        aria-describedby="lc-dialog-text"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="lc-dialog__edge" aria-hidden="true" />
        <h2 className="lc-dialog__title" id="lc-dialog-title">{props.title}</h2>
        <div className="lc-dialog__text" id="lc-dialog-text">{props.children}</div>
        <div className="lc-dialog__actions">
          <button ref={cancelRef} type="button" className="lc-btn lc-btn--ghost" onClick={props.onClose}>
            {props.cancelLabel ?? 'Annuler'}
          </button>
          <button
            type="button"
            className={`lc-btn ${props.danger ? 'lc-btn--danger' : 'lc-btn--primary'}`}
            onClick={props.onConfirm}
          >
            {props.confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
